import { assertNever } from "@redotech/util/type";

export enum ListToListMatchType {
  INCLUDES_ANY_OF = "INCLUDES_ANY_OF",
  INCLUDES_ALL_OF = "INCLUDES_ALL_OF",
  HAS_NONE_OF = "HAS_NONE_OF",
}

export enum StringToListMatchType {
  IS_ANY_OF = "IS_ANY_OF",
  IS_NONE_OF = "IS_NONE_OF",
  EXISTS = "EXISTS",
}

const stringToListMatchTypeToLabel: Record<StringToListMatchType, string> = {
  [StringToListMatchType.IS_ANY_OF]: "is any of",
  [StringToListMatchType.IS_NONE_OF]: "is none of",
  [StringToListMatchType.EXISTS]: "exists",
};

/**
 * Ex: Order tags include any of
 */
const listToListMatchTypeToLabel: Record<ListToListMatchType, string> = {
  [ListToListMatchType.INCLUDES_ANY_OF]: "include any of",
  [ListToListMatchType.INCLUDES_ALL_OF]: "include all of",
  [ListToListMatchType.HAS_NONE_OF]: "have none of",
};

/**
 * Ex: Order tag list includes any of
 */
const listToListMatchTypeToPluralLabel: Record<ListToListMatchType, string> = {
  [ListToListMatchType.INCLUDES_ANY_OF]: "includes any of",
  [ListToListMatchType.INCLUDES_ALL_OF]: "includes all of",
  [ListToListMatchType.HAS_NONE_OF]: "has none of",
};

export const getMatchTypeLabel = (
  matchType: StringToListMatchType | ListToListMatchType,
  isPlural: boolean,
) => {
  if (matchType in StringToListMatchType) {
    return stringToListMatchTypeToLabel[matchType as StringToListMatchType];
  }
  if (isPlural) {
    return listToListMatchTypeToPluralLabel[matchType as ListToListMatchType];
  }
  return listToListMatchTypeToLabel[matchType as ListToListMatchType];
};

export function checkExactMatch({
  listFromGroup,
  listToMatch,
  matchType,
}: {
  listFromGroup: string[];
  listToMatch: string[];
  matchType: ListToListMatchType | undefined;
}): { passes: boolean; filteredFGroupList: string[] } {
  if (!matchType) {
    return { passes: false, filteredFGroupList: [] };
  }
  let passes: boolean;
  let itemsThatMatch: string[];
  switch (matchType) {
    case ListToListMatchType.INCLUDES_ANY_OF:
      itemsThatMatch = listFromGroup.filter((item) =>
        listToMatch.includes(item),
      );
      return {
        passes: itemsThatMatch.length > 0,
        filteredFGroupList: itemsThatMatch,
      };
    case ListToListMatchType.INCLUDES_ALL_OF:
      passes = listToMatch.every((item) => listFromGroup.includes(item));

      return { passes, filteredFGroupList: passes ? listFromGroup : [] };
    case ListToListMatchType.HAS_NONE_OF:
      passes = listToMatch.every((item) => !listFromGroup.includes(item));
      return { passes, filteredFGroupList: passes ? listFromGroup : [] };
    default:
      return assertNever(matchType);
  }
}

export function checkListContains({
  listFromGroup,
  listToMatch,
  matchType,
}: {
  listFromGroup: string[];
  listToMatch: string[];
  matchType: ListToListMatchType | undefined;
}): { passes: boolean; filteredFGroupList: string[] } {
  if (!matchType) {
    return { passes: false, filteredFGroupList: [] };
  }
  let passes: boolean;
  let itemsThatMatch: string[];
  switch (matchType) {
    case ListToListMatchType.INCLUDES_ANY_OF:
      itemsThatMatch = listFromGroup.filter((listFromGroupItem) =>
        listToMatch.some((listToMatchItem) =>
          listFromGroupItem.includes(listToMatchItem),
        ),
      );
      return {
        passes: itemsThatMatch.length > 0,
        filteredFGroupList: itemsThatMatch,
      };
    case ListToListMatchType.INCLUDES_ALL_OF:
      passes = listToMatch.every((listToMatchItem) =>
        listFromGroup.some((listFromGroupItem) =>
          listFromGroupItem.includes(listToMatchItem),
        ),
      );
      return { passes, filteredFGroupList: passes ? listFromGroup : [] }; // all or nothing
    case ListToListMatchType.HAS_NONE_OF:
      passes = listToMatch.every((listToMatchItem) =>
        listFromGroup.some(
          (listFromGroupItem) => !listFromGroupItem.includes(listToMatchItem),
        ),
      );
      return { passes, filteredFGroupList: passes ? listFromGroup : [] }; // all or nothing
    default:
      return assertNever(matchType);
  }
}
